import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import type { SplashDetail } from 'contexts/SplashScreenContext';
import { consumeSplashContinuation, rememberSplashQuip } from 'services/splashContinuity';
import Logo from 'assets/images/hjlogo.png';

import {
  SPLASH_ACCENT,
  SPLASH_BACKDROP,
  SPLASH_FONT,
  SPLASH_LOGO_WIDTH
} from './splashTheme';

const jackQuips = [
  "Sometimes it's luck, sometimes you suck.",
  "Fold your laundry, not your aces.",
  "The chips don't lie, but your face might.",
  "Go big or go home. Actually, you can't go home — you're online.",
  "If you can't spot the fish... check your mirror.",
  "Patience is a virtue. Slow-rolling is not.",
  "Your poker face needs work. I can see it through the screen.",
  "All-in on vibes.",
  "May the flop be ever in your favor.",
  "Raise your standards. And your bets.",
  "Bluff responsibly.",
  "The house always wins. Good thing this isn't a house.",
  "Shuffle up and deal with it.",
  "You miss 100% of the bluffs you don't make.",
  "Keep calm and call the river.",
];

/**
 * One source of truth for the drawn width and the printed number.
 *
 * The Number.isFinite guard is not defensive padding: NaN survives both
 * Math.min and Math.max, so a bad percent reached the DOM as `width: NaN%`
 * (which the browser drops, leaving the bar at its previous width) next to a
 * label reading "NaN%". This value crosses a service boundary from the OTA
 * plugin's progress events, so treat a non-number as no progress yet.
 */
const clampPercent = (percent: number): number =>
  Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;

const pickQuip = () => jackQuips[Math.floor(Math.random() * jackQuips.length)];

/**
 * Decide this mount's quip, and whether it is continuing a splash the player
 * is already looking at.
 *
 * Only the persistent (loading-gate) splash can continue. The one-shot intro
 * is a separate moment with its own quip and its own animation, and applying
 * an update mid-session is not a thing that happens, so it never asks.
 */
const openQuip = (
  persist: boolean
): { quip: string; continued: boolean; carriedDetail?: SplashDetail } => {
  const carried = persist ? consumeSplashContinuation() : null;
  const quip = carried?.quip ?? pickQuip();
  if (persist) rememberSplashQuip(quip);
  return { quip, continued: carried !== null, carriedDetail: carried?.detail };
};

type SplashScreenProps = {
  onComplete: () => void;
  /** When true, the splash stays visible (no auto-fadeout). Used for loading states. */
  persist?: boolean;
  /**
   * Optional progress readout (cold-launch OTA download). Rendered under the
   * byline; absent for every other loading gate, which stays visually
   * unchanged. It intentionally does NOT participate in the GSAP timeline —
   * the bar has to update continuously after the intro tweens have finished.
   */
  detail?: SplashDetail;
};

const SplashScreen = ({ onComplete, persist = false, detail }: SplashScreenProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const quipRef = useRef<HTMLDivElement>(null);
  const bylineRef = useRef<HTMLDivElement>(null);
  // Read once, on this instance's first render, so the answer is stable for
  // the life of the mount.
  const [{ quip, continued, carriedDetail }] = useState(() => openQuip(persist));
  // The page after an apply has no progress of its own to report, so it shows
  // the row the previous page left behind. A live detail always wins: if the
  // gate starts reporting again, that is the truth.
  const shownDetail = detail ?? carriedDetail;

  useEffect(() => {
    const container = containerRef.current;
    const logo = logoRef.current;
    const quipEl = quipRef.current;
    const byline = bylineRef.current;
    if (!container || !logo || !quipEl || !byline) return;

    // Continuing the splash from before an OTA reload: everything is already
    // rendered at the intro's end state (see the opacity props below), so
    // there is nothing to animate. Replaying the fade here is what made one
    // continuous wait look like two separate screens.
    if (continued) return;

    const tl = gsap.timeline({
      onComplete: () => {
        if (!persist) onComplete();
      }
    });

    // Phase 1: Logo fades in and scales up
    tl.fromTo(logo,
      { opacity: 0, scale: 0.7 },
      { opacity: 1, scale: 1, duration: 0.6, ease: 'power3.out' }
    )
    // Phase 2: Quip types in
    .fromTo(quipEl,
      { opacity: 0, y: 15 },
      { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' },
      '-=0.1'
    )
    // Byline
    .fromTo(byline,
      { opacity: 0 },
      { opacity: 0.5, duration: 0.3 },
      '-=0.1'
    );

    if (!persist) {
      // Phase 3: Hold for reading
      tl.to({}, { duration: 1.8 })
      // Phase 4: Everything fades out together
      .to(container, {
        opacity: 0,
        duration: 0.5,
        ease: 'power2.in'
      });
    }

  }, []);

  return (
    <div ref={containerRef} style={containerStyle}>
      <div style={contentStyle}>
        <img
          ref={logoRef}
          src={Logo}
          alt="Hijack Poker"
          style={{ ...logoStyle, opacity: continued ? 1 : 0 }}
        />
        <div ref={quipRef} style={{ ...quipStyle, opacity: continued ? 1 : 0 }}>
          "{quip}"
        </div>
        <div ref={bylineRef} style={{ ...bylineStyle, opacity: continued ? 0.5 : 0 }}>
          — Jack
        </div>
        {/* Stays up until the splash itself gives way, including across the
            apply reload. Dropping it the moment the new page took over both
            lost the "100%" the player had just watched fill, and re-centred
            the column: measured on device, the logo jumped 58px at that
            instant, which reads as the screen changing all on its own. */}
        {shownDetail && (
          <div style={progressWrapStyle} role='status' aria-live='polite'>
            <div style={progressTrackStyle}>
              <div
                style={{ ...progressBarStyle, width: `${clampPercent(shownDetail.percent)}%` }}
              />
            </div>
            <div style={progressLabelStyle}>
              <span>{shownDetail.label}</span>
              {/* Same clamped value as the bar. These were computed separately,
                  so an out-of-range percent drew a full bar next to a label
                  reading "420%". */}
              <span style={progressPercentStyle}>
                {Math.round(clampPercent(shownDetail.percent))}%
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SplashScreen;

const containerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100vh',
  background: SPLASH_BACKDROP,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1.5rem'
};

const logoStyle: CSSProperties = {
  width: SPLASH_LOGO_WIDTH,
  opacity: 0
};

const quipStyle: CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.8)',
  fontStyle: 'italic',
  textAlign: 'center',
  maxWidth: '400px',
  lineHeight: 1.5,
  fontFamily: SPLASH_FONT,
  opacity: 0
};

// Sized off the quip's max-width so the bar lines up with the copy above it,
// and capped at the viewport so landscape phones (where 400px can exceed the
// usable width) never push the layout sideways.
const progressWrapStyle: CSSProperties = {
  width: 'min(320px, calc(100vw - 3rem))',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  marginTop: '0.5rem'
};

const progressTrackStyle: CSSProperties = {
  width: '100%',
  height: '6px',
  borderRadius: '999px',
  background: 'rgba(255, 255, 255, 0.12)',
  overflow: 'hidden'
};

const progressBarStyle: CSSProperties = {
  height: '100%',
  borderRadius: '999px',
  background: SPLASH_ACCENT,
  boxShadow: '0 0 12px rgba(233, 140, 38, 0.5)',
  transition: 'width 0.25s ease-out'
};

const progressLabelStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: '0.75rem',
  fontSize: '0.8rem',
  fontFamily: SPLASH_FONT,
  color: 'rgba(255, 255, 255, 0.7)'
};

const progressPercentStyle: CSSProperties = {
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)',
  fontVariantNumeric: 'tabular-nums'
};

const bylineStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 800,
  color: SPLASH_ACCENT,
  fontFamily: SPLASH_FONT,
  letterSpacing: '0.05em',
  textShadow: '0 0 10px rgba(233, 140, 38, 0.4)',
  opacity: 0
};
